import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import TrDateInput from '../components/TrDateInput';
import { useTranslation } from 'react-i18next';

interface SunumDaire {
  sunumDaireID: number;
  daireID: number;
  daireNo: string | null;
  projeAdi: string | null;
  katAdi: string | null;
  cepheAdi: string | null;
  listeFiyati: number | null;
  durum: string;
  musteriTepkisi: string;
}

interface MusteriSunumItem {
  sunumID: number;
  takipNo: string;
  cariID: number;
  cariAdSoyad: string | null;
  cariFirma: string | null;
  cariTelefon: string | null;
  olusturanKullaniciID: number;
  olusturanAdSoyad: string | null;
  baslik: string | null;
  durum: string;
  olusturmaTarihi: string;
  guncellemeTarihi: string | null;
  aktifMi: boolean;
  daireAdet: number;
  daireler: SunumDaire[];
}

interface Cari {
  cariID: number;
  adiSoyadi: string;
  firmaAdi: string | null;
  telefon1: string;
}

interface DaireItem {
  daireID: number;
  daireNo: string;
  takipNo: string;
  daireDurumu: string;
  projeID: number;
  katID: number;
  cepheID: number;
  listeFiyati: number | null;
  netM2: number | null;
  aktifMi: boolean;
  proje: { projeID: number; projeAdi: string } | null;
  blok: { blokID: number; blokAdi: string } | null;
  kat: { katID: number; katAdi: string } | null;
  cephe: { cepheID: number; cepheAdi: string } | null;
  daireTipi: { daireTipID: number; daireTipiAdi: string } | null;
}

interface PortfoyItem {
  portfoyID: number;
  baslik: string;
  adres: string | null;
  fiyat: number;
  ilanNo: string;
  tur: string;
  aktifMi: boolean;
}

const DURUM_LABELS: Record<string, string> = {
  'Taslak': 'Taslak',
  'Devam Ediyor': 'Devam Ediyor',
  'Tamamlandı': 'Tamamlandı',
  'Olumlu Sonuçlandı': 'Olumlu Sonuçlandı',
  'Olumsuz Sonuçlandı': 'Olumsuz Sonuçlandı',
  'İptal Edildi': 'İptal Edildi',
};

const DURUM_BADGE: Record<string, { bg: string; color: string }> = {
  'Taslak':             { bg: '#f1f5f9', color: '#475569' },
  'Devam Ediyor':       { bg: '#eff6ff', color: '#1d4ed8' },
  'Tamamlandı':         { bg: '#f0fdf4', color: '#15803d' },
  'Olumlu Sonuçlandı':  { bg: '#f0fdf4', color: '#059669' },
  'Olumsuz Sonuçlandı': { bg: '#fff1f2', color: '#be123c' },
  'İptal Edildi':       { bg: '#fef3c7', color: '#92400e' },
};

const DURUM_BAR: Record<string, string> = {
  'Taslak': '#94a3b8',
  'Devam Ediyor': '#3b82f6',
  'Tamamlandı': '#10b981',
  'Olumlu Sonuçlandı': '#059669',
  'Olumsuz Sonuçlandı': '#ef4444',
  'İptal Edildi': '#d97706',
};

const MusteriSunumlari: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [sunumlar, setSunumlar] = useState<MusteriSunumItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterDurumlar, setFilterDurumlar] = useState<string[]>(['Taslak', 'Devam Ediyor']);
  const [filterTarih, setFilterTarih] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  // Durum değiştirme
  const [durumUpdating, setDurumUpdating] = useState<number | null>(null);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sunumBaslik, setSunumBaslik] = useState('');
  const [creating, setCreating] = useState(false);

  // Müşteri seçimi
  const [cariler, setCariler] = useState<Cari[]>([]);
  const [cariSearch, setCariSearch] = useState('');
  const [selectedCari, setSelectedCari] = useState<Cari | null>(null);

  // Hızlı Müşteri Ekle
  const [showQuickCariModal, setShowQuickCariModal] = useState(false);
  const [quickCariSaving, setQuickCariSaving] = useState(false);
  const [quickCariForm, setQuickCariForm] = useState({ adiSoyadi: '', telefon1: '', ePosta: '' });

  // Rapor modal
  const [showRaporModal, setShowRaporModal] = useState(false);
  const [raporBaslangic, setRaporBaslangic] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  });
  const [raporBitis, setRaporBitis] = useState(() => new Date().toISOString().split('T')[0]);
  const [raporProjeFilter, setRaporProjeFilter] = useState('');
  const [raporDaireler, setRaporDaireler] = useState<DaireItem[]>([]);
  const [raporDairelerLoading, setRaporDairelerLoading] = useState(false);

  // Daire seçimi
  const [tumDaireler, setTumDaireler] = useState<DaireItem[]>([]);
  const [filterProjeID, setFilterProjeID] = useState('');
  const [filterKatID, setFilterKatID] = useState('');
  const [filterCepheID, setFilterCepheID] = useState('');
  const [selectedDaireler, setSelectedDaireler] = useState<number[]>([]);
  const [daireSearch, setDaireSearch] = useState('');

  // Emlak portföyü seçimi
  const [addKaynakTuru, setAddKaynakTuru] = useState<'Daire' | 'Portfoy'>('Daire');
  const [tumPortfoyler, setTumPortfoyler] = useState<PortfoyItem[]>([]);
  const [portfoySearch, setPortfoySearch] = useState('');
  const [selectedPortfoyler, setSelectedPortfoyler] = useState<number[]>([]);

  useEffect(() => {
    fetchSunumlar();
  }, []);

  const fetchSunumlar = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/api/MusteriSunumlari');
      setSunumlar(res.data);
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCariler = async () => {
    try {
      const res = await axiosInstance.get('/api/Cariler?tur=M%C3%BC%C5%9Fteri');
      setCariler(res.data);
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const fetchDaireler = async () => {
    try {
      const res = await axiosInstance.get('/api/Daireler?aktifMi=true');
      const satistakiler = (res.data as DaireItem[]).filter(d => d.daireDurumu === 'Satışta');
      setTumDaireler(satistakiler);
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const fetchPortfoyler = async () => {
    try {
      const res = await axiosInstance.get('/api/EmlakPortfoy?aktifMi=true');
      setTumPortfoyler(res.data as PortfoyItem[]);
    } catch {
      // portföy listesi opsiyonel — sessizce geç
    }
  };

  const openRaporModal = async () => {
    setShowRaporModal(true);
    if (raporDaireler.length === 0) {
      setRaporDairelerLoading(true);
      try {
        const res = await axiosInstance.get('/api/Daireler?aktifMi=true');
        const satistakiler = (res.data as DaireItem[]).filter(d => d.daireDurumu === 'Satışta');
        setRaporDaireler(satistakiler);
      } catch {
        toast.error(t('msg.islemHatasi'));
      } finally {
        setRaporDairelerLoading(false);
      }
    }
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    setSunumBaslik('');
    setSelectedCari(null);
    setCariSearch('');
    setSelectedDaireler([]);
    setFilterProjeID('');
    setFilterKatID('');
    setFilterCepheID('');
    setDaireSearch('');
    setAddKaynakTuru('Daire');
    setSelectedPortfoyler([]);
    setPortfoySearch('');
    fetchCariler();
    fetchDaireler();
    fetchPortfoyler();
  };

  const handleCreate = async () => {
    if (!selectedCari) { toast.warning(t('msg.uyari')); return; }
    try {
      setCreating(true);
      const res = await axiosInstance.post('/api/MusteriSunumlari', {
        cariID: selectedCari.cariID,
        olusturanKullaniciID: user?.kullaniciID,
        baslik: sunumBaslik || null,
        daireIDler: selectedDaireler,
      });
      const sunumID = res.data.sunumID;
      if (selectedPortfoyler.length > 0) {
        const ogeler = selectedPortfoyler.map(id => ({ ogeTuru: 'Portfoy', gayrimenkulID: id }));
        await axiosInstance.post(`/api/MusteriSunumlari/${sunumID}/ogeler`, ogeler).catch(() => {
          toast.warning('Sunum oluşturuldu ancak portföy öğeleri eklenemedi, sunum ekranından tekrar deneyin.');
        });
      }
      toast.success(t('msg.islemBasarili'));
      setShowCreateModal(false);
      navigate(`/musteri-sunumlari/${sunumID}`);
    } catch (err: any) {
      // 409: müşterinin Taslak/Devam Ediyor durumunda sunumu var — yenisi açılmaz
      if (err?.response?.status === 409) {
        const d = err.response.data;
        toast.warning(d?.message || 'Bu müşterinin devam eden bir sunumu var.', { autoClose: 6000 });
        if (d?.mevcutSunumID) {
          setShowCreateModal(false);
          navigate(`/musteri-sunumlari/${d.mevcutSunumID}`);
        }
      } else {
        toast.error(t('msg.islemHatasi'));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleQuickAddCari = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickCariForm.adiSoyadi.trim()) { toast.warning('Ad Soyad zorunludur.'); return; }
    if (!quickCariForm.telefon1.trim()) { toast.warning('Telefon zorunludur.'); return; }
    try {
      setQuickCariSaving(true);
      const res = await axiosInstance.post('/api/Cariler', {
        adiSoyadi: quickCariForm.adiSoyadi.trim(),
        telefon1: quickCariForm.telefon1.trim(),
        ePosta: quickCariForm.ePosta.trim() || null,
        turu: 'Müşteri',
        aktifMi: true,
      });
      const yeniCari: Cari = {
        cariID: res.data.cariID,
        adiSoyadi: res.data.adiSoyadi,
        firmaAdi: res.data.firmaAdi || null,
        telefon1: res.data.telefon1,
      };
      setCariler(prev => [yeniCari, ...prev]);
      setSelectedCari(yeniCari);
      if (!sunumBaslik.trim()) setSunumBaslik(`${yeniCari.adiSoyadi} Sunumu`);
      setShowQuickCariModal(false);
      setQuickCariForm({ adiSoyadi: '', telefon1: '', ePosta: '' });
      toast.success(t('msg.islemBasarili'));
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setQuickCariSaving(false);
    }
  };

  const handleDelete = async (id: number) => {    try {
      await axiosInstance.delete(`/api/MusteriSunumlari/${id}`);
      toast.success(t('msg.islemBasarili'));
      setDeleteConfirm(null);
      fetchSunumlar();
    } catch {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleDurumChange = async (sunumID: number, yeniDurum: string) => {
    try {
      setDurumUpdating(sunumID);
      await axiosInstance.put(`/api/MusteriSunumlari/${sunumID}`, { durum: yeniDurum });
      setSunumlar(prev => prev.map(s => s.sunumID === sunumID ? { ...s, durum: yeniDurum } : s));
    } catch {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setDurumUpdating(null);
    }
  };

  // Filtre türetme
  const projeler = Array.from(
    new Map(tumDaireler.filter(d => d.proje).map(d => [d.proje!.projeID, d.proje!])).values()
  );
  const katlar = Array.from(
    new Map(
      tumDaireler
        .filter(d => d.kat && (!filterProjeID || d.proje?.projeID.toString() === filterProjeID))
        .map(d => [d.kat!.katID, d.kat!])
    ).values()
  );
  const cepheler = Array.from(
    new Map(
      tumDaireler
        .filter(d => d.cephe && (!filterProjeID || d.proje?.projeID.toString() === filterProjeID))
        .map(d => [d.cephe!.cepheID, d.cephe!])
    ).values()
  );

  const filteredDaireler = tumDaireler.filter(d => {
    if (filterProjeID && d.proje?.projeID.toString() !== filterProjeID) return false;
    if (filterKatID && d.kat?.katID.toString() !== filterKatID) return false;
    if (filterCepheID && d.cephe?.cepheID.toString() !== filterCepheID) return false;
    if (daireSearch) {
      const s = daireSearch.toLowerCase();
      return (
        d.daireNo.toLowerCase().includes(s) ||
        (d.proje?.projeAdi.toLowerCase().includes(s) ?? false) ||
        (d.kat?.katAdi.toLowerCase().includes(s) ?? false)
      );
    }
    return true;
  });

  const filteredPortfoyler = tumPortfoyler.filter(p => {
    if (!portfoySearch) return true;
    const s = portfoySearch.toLowerCase();
    return (
      p.baslik.toLowerCase().includes(s) ||
      (p.adres?.toLowerCase().includes(s) ?? false) ||
      p.ilanNo.toLowerCase().includes(s)
    );
  });

  const filteredCariler = cariler.filter(c =>
    !cariSearch ||
    c.adiSoyadi.toLowerCase().includes(cariSearch.toLowerCase()) ||
    (c.firmaAdi && c.firmaAdi.toLowerCase().includes(cariSearch.toLowerCase())) ||
    c.telefon1.includes(cariSearch)
  );

  const filteredSunumlar = sunumlar.filter(s => {
    if (!s.aktifMi) return false;
    const matchSearch = !searchText ||
      (s.baslik && s.baslik.toLowerCase().includes(searchText.toLowerCase())) ||
      (s.cariAdSoyad && s.cariAdSoyad.toLowerCase().includes(searchText.toLowerCase())) ||
      s.takipNo.toLowerCase().includes(searchText.toLowerCase());
    const matchDurum = filterDurumlar.length === 0 || filterDurumlar.includes(s.durum);
    const matchTarih = !filterTarih || s.olusturmaTarihi.startsWith(filterTarih);
    return matchSearch && matchDurum && matchTarih;
  });

  // Rapor hesaplamaları
  const raporSunumlar = sunumlar.filter(s => {
    if (!s.aktifMi) return false;
    const dt = s.olusturmaTarihi.split('T')[0];
    if (raporBaslangic && dt < raporBaslangic) return false;
    if (raporBitis && dt > raporBitis) return false;
    if (raporProjeFilter) {
      const projeMatch = s.daireler.some(d => d.projeAdi === raporProjeFilter);
      if (!projeMatch) return false;
    }
    return true;
  });

  const raporProjeler = sunumlar
    .filter(s => s.aktifMi)
    .flatMap(s => s.daireler.map(d => d.projeAdi))
    .filter((p): p is string => Boolean(p))
    .filter((p, i, arr) => arr.indexOf(p) === i);

  const raporByProje: Record<string, { count: number; daireSayisi: number }> = {};
  raporSunumlar.forEach(s => {
    s.daireler.forEach(d => {
      const proje = d.projeAdi || 'Diğer';
      if (!raporByProje[proje]) raporByProje[proje] = { count: 0, daireSayisi: 0 };
      raporByProje[proje].daireSayisi++;
    });
    const projeler = s.daireler
      .map(d => d.projeAdi || 'Diğer')
      .filter((p, i, arr) => arr.indexOf(p) === i);
    projeler.forEach(p => { raporByProje[p].count++; });
  });

  const raporByAy: Record<string, number> = {};
  raporSunumlar.forEach(s => {
    const ay = s.olusturmaTarihi.substring(0, 7);
    raporByAy[ay] = (raporByAy[ay] || 0) + 1;
  });

  const raporByDaire: Record<number, { sunumSayisi: number; sunumlar: { sunumID: number; baslik: string | null; cariAdSoyad: string | null; durum: string; daireDurum: string; musteriTepkisi: string; tarih: string }[] }> = {};
  raporSunumlar.forEach(s => {
    s.daireler.forEach(d => {
      if (!raporByDaire[d.daireID]) raporByDaire[d.daireID] = { sunumSayisi: 0, sunumlar: [] };
      raporByDaire[d.daireID].sunumSayisi++;
      raporByDaire[d.daireID].sunumlar.push({
        sunumID: s.sunumID,
        baslik: s.baslik,
        cariAdSoyad: s.cariAdSoyad,
        durum: s.durum,
        daireDurum: d.durum,
        musteriTepkisi: d.musteriTepkisi,
        tarih: s.olusturmaTarihi,
      });
    });
  });

  const formatDate = (d: string) => new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const formatFiyat = (f: number | null) => f ? f.toLocaleString('tr-TR') + ' ₺' : '—';

  // Styles
  const selSt: React.CSSProperties = {
    padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7,
    fontSize: 12.5, background: '#fff', color: '#374151', outline: 'none', width: '100%',
  };
  const inputSt: React.CSSProperties = {
    width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box',
  };
  const labelSt: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6, display: 'block' };

  return (
    <div style={{ padding: 'clamp(12px, 4vw, 1.5rem)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          {/* Sol menüdeki adla aynı: menu.sunumTeklif (çeviri yoksa yedek metne düşer) */}
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1a2440', margin: 0 }}>{t('menu.sunumTeklif', 'Sunum & Teklif')}</h1>
          <p style={{ fontSize: 13.5, color: '#64748b', margin: '4px 0 0' }}>Daire sunumlarınızı ve müşteri görüşmelerinizi organize edin.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={openRaporModal}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 9, padding: '9px 16px',
              fontSize: 13.5, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.borderColor = '#d1d5db'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
            {t('btn.rapor', 'Rapor')}
          </button>
          <button
            onClick={openCreateModal}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#1a2440', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 18px',
              fontSize: 13.5, fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#e67d21')}
            onMouseLeave={e => (e.currentTarget.style.background = '#1a2440')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {t('btn.yeniSunum', 'Yeni Sunum')}
          </button>
        </div>
      </div>

      {/* Filtreler */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
        <div style={{ position: 'relative', flex: '1 1 180px', minWidth: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input type="text" style={{ paddingLeft: 32, height: 36, border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: '100%', outline: 'none', background: '#fff', boxSizing: 'border-box' }}
            placeholder={t('label.ara')} value={searchText} onChange={e => setSearchText(e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', flex: '1 1 100%' }}>
          {Object.entries(DURUM_LABELS).map(([key, label]) => {
            const active = filterDurumlar.includes(key);
            return (
              <button key={key}
                onClick={() => setFilterDurumlar(prev => prev.includes(key) ? prev.filter(d => d !== key) : [...prev, key])}
                style={{
                  padding: '4px 11px', borderRadius: 9999, fontSize: 12, fontWeight: 500,
                  border: active ? '1px solid #e67d21' : '1px solid transparent',
                  background: active ? '#fff7ed' : '#f8fafc',
                  color: active ? '#c2410c' : '#4b5563',
                  cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                }}>{t('sunum.durum.' + key.replace(/\s/g, '_'), label)}</button>
            );
          })}
          {filterDurumlar.length > 0 && (
            <button onClick={() => setFilterDurumlar([])}
              style={{ fontSize: 12, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>
              {t('btn.temizle', 'Temizle')}
            </button>
          )}
        </div>

        <TrDateInput value={filterTarih} onChange={e => setFilterTarih(e.target.value)}
          style={{ height: 36, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', outline: 'none' }} />
      </div>

      {/* Liste */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>{t('msg.yukleniyor')}</div>
      ) : filteredSunumlar.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
          <i className="fas fa-folder-open" style={{ fontSize: 32, marginBottom: 12, display: 'block' }}></i>
          <p>{t('msg.sunumYok', 'Henüz sunum oluşturulmamış.')}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredSunumlar.map(sunum => {
            const badge = DURUM_BADGE[sunum.durum] || { bg: '#f1f5f9', color: '#475569' };
            return (
              <div key={sunum.sunumID}
                style={{
                  display: 'flex', alignItems: 'stretch', background: '#fff',
                  border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer', transition: 'box-shadow 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)')}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)')}
                onClick={() => navigate(`/musteri-sunumlari/${sunum.sunumID}`)}
              >
                {/* Durum çubuğu */}
                <div style={{ width: 4, flexShrink: 0, background: DURUM_BAR[sunum.durum] || '#94a3b8' }} />

                {/* İçerik */}
                <div style={{ flex: 1, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {sunum.baslik || (sunum.cariAdSoyad + ' Sunumu')}
                      </h3>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 9999, background: badge.bg, color: badge.color, whiteSpace: 'nowrap' }}>
                        {t('sunum.durum.' + sunum.durum.replace(/\s/g, '_'), sunum.durum)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', marginTop: 5 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#1e293b' }}>{sunum.cariAdSoyad}</span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>{sunum.takipNo}</span>
                      </div>
                      {sunum.cariTelefon && <span style={{ fontSize: 12, color: '#64748b' }}>{sunum.cariTelefon}</span>}
                      <span style={{ fontSize: 12, color: '#64748b' }}>{formatDate(sunum.olusturmaTarihi)}</span>
                      {sunum.olusturanAdSoyad && <span style={{ fontSize: 12, color: '#94a3b8' }}>{sunum.olusturanAdSoyad}</span>}
                    </div>
                  </div>

                  {/* Daire sayısı + Durum seç + Sil */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#1a2440', lineHeight: 1 }}>{sunum.daireAdet}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>Daire</div>
                    </div>

                    <select
                      value={sunum.durum}
                      disabled={durumUpdating === sunum.sunumID}
                      onChange={e => handleDurumChange(sunum.sunumID, e.target.value)}
                      style={{ ...selSt, width: 130, fontSize: 12 }}
                    >
                      {Object.keys(DURUM_LABELS).map(d => <option key={d} value={d}>{t('sunum.durum.' + d.replace(/\s/g, '_'), d)}</option>)}
                    </select>

                    <button
                      onClick={() => setDeleteConfirm(sunum.sunumID)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#94a3b8', borderRadius: 6, display: 'flex', alignItems: 'center' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                      title={t('btn.sil')}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Silme Onayı */}
      {deleteConfirm !== null && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setDeleteConfirm(null)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 14, padding: 28, maxWidth: 360, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🗑️</div>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: '#111827', marginBottom: 8 }}>Sunumu Sil</h3>
            <p style={{ fontSize: 13.5, color: '#6b7280', marginBottom: 22 }}>Bu sunum silinecek. Bu işlemi geri alamazsınız.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setDeleteConfirm(null)}
                style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer', fontWeight: 500 }}>
                İptal
              </button>
              <button onClick={() => handleDelete(deleteConfirm)}
                style={{ padding: '8px 20px', border: 'none', borderRadius: 8, background: '#ef4444', color: '#fff', fontSize: 13.5, cursor: 'pointer', fontWeight: 600 }}>
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Sunum Modal */}
      {showCreateModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setShowCreateModal(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }} />
          <div style={{ position: 'relative', width: '100%', maxWidth: 700, maxHeight: '92vh', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', borderRadius: 16, overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={{ flexShrink: 0, padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>{t('sunum.yeniSunumOlustur', 'Yeni Sunum Oluştur')}</h2>
              </div>
              <button onClick={() => setShowCreateModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#9ca3af', borderRadius: 6, display: 'flex', alignItems: 'center' }}>
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Başlık */}
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 12, alignItems: 'center' }}>
                <label style={{ ...labelSt, margin: 0, whiteSpace: 'nowrap', display: 'flex', gap: 6, alignItems: 'center' }}>
                  Sunum Başlığı <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(opsiyonel)</span>
                </label>
                <input type="text" style={{ ...inputSt, margin: 0 }} placeholder={t('label.ara')}
                  value={sunumBaslik} onChange={e => setSunumBaslik(e.target.value)} />
              </div>

              {/* Müşteri */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label style={{ ...labelSt, marginBottom: 0 }}>Müşteri <span style={{ color: '#ef4444' }}>*</span></label>
                  {!selectedCari && (
                    <button
                      type="button"
                      onClick={() => { setShowQuickCariModal(true); setQuickCariForm({ adiSoyadi: '', telefon1: '', ePosta: '' }); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500, color: '#1a2440', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                    >
                      <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> Hızlı Müşteri Ekle
                    </button>
                  )}
                </div>
                {selectedCari ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>{selectedCari.adiSoyadi}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{selectedCari.telefon1}</div>
                      </div>
                      {selectedCari.firmaAdi && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{selectedCari.firmaAdi}</div>}
                    </div>
                    <button onClick={() => {
                        if (sunumBaslik === `${selectedCari.adiSoyadi} Sunumu`) setSunumBaslik('');
                        setSelectedCari(null);
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4, borderRadius: 6 }}>
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div>
                    <input type="text" style={{ ...inputSt, marginBottom: 8 }} placeholder={t('label.ara')}
                      value={cariSearch} onChange={e => setCariSearch(e.target.value)} />
                    <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                      {filteredCariler.length === 0 ? (
                        <div style={{ padding: '12px 16px', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>Müşteri bulunamadı</div>
                      ) : filteredCariler.map(c => (
                        <div key={c.cariID}
                          onClick={() => {
                            setSelectedCari(c);
                            if (!sunumBaslik.trim()) {
                              setSunumBaslik(`${c.adiSoyadi} Sunumu`);
                            }
                          }}
                          style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.12s' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                          <div style={{ fontSize: 13.5, fontWeight: 500, color: '#111827' }}>{c.adiSoyadi}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>{c.firmaAdi ? `${c.firmaAdi} • ` : ''}{c.telefon1}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Daire / Portföy Seçimi */}
              <div>
                <label style={labelSt}>
                  Daireler / Emlak Portföyü <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(opsiyonel — boş da oluşturabilirsiniz, sonra sunum ekranından eklenebilir)</span>
                </label>

                {/* Kaynak sekmeleri */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 10, borderBottom: '1px solid #e5e7eb' }}>
                  <button type="button" onClick={() => setAddKaynakTuru('Daire')}
                    style={{
                      padding: '7px 14px', border: 'none', borderBottom: addKaynakTuru === 'Daire' ? '2px solid #1a2440' : '2px solid transparent',
                      background: 'none', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                      color: addKaynakTuru === 'Daire' ? '#1a2440' : '#94a3b8', marginBottom: -1,
                    }}>
                    <i className="fas fa-home" style={{ marginRight: 6 }} />Projeden Daire
                    {selectedDaireler.length > 0 && <span style={{ marginLeft: 6, background: '#1a2440', color: '#fff', borderRadius: 9999, padding: '1px 7px', fontSize: 11 }}>{selectedDaireler.length}</span>}
                  </button>
                  <button type="button" onClick={() => setAddKaynakTuru('Portfoy')}
                    style={{
                      padding: '7px 14px', border: 'none', borderBottom: addKaynakTuru === 'Portfoy' ? '2px solid #e67d21' : '2px solid transparent',
                      background: 'none', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                      color: addKaynakTuru === 'Portfoy' ? '#e67d21' : '#94a3b8', marginBottom: -1,
                    }}>
                    <i className="fas fa-building" style={{ marginRight: 6 }} />Emlak Portföyü
                    {selectedPortfoyler.length > 0 && <span style={{ marginLeft: 6, background: '#e67d21', color: '#fff', borderRadius: 9999, padding: '1px 7px', fontSize: 11 }}>{selectedPortfoyler.length}</span>}
                  </button>
                </div>

                {addKaynakTuru === 'Daire' ? (
                  <>
                    {/* Filtreler */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                      <select value={filterProjeID} onChange={e => { setFilterProjeID(e.target.value); setFilterKatID(''); setFilterCepheID(''); }}
                        style={{ ...selSt, flex: '1 1 130px' }}>
                        <option value="">Tüm Projeler</option>
                        {projeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}
                      </select>
                      <select value={filterKatID} onChange={e => setFilterKatID(e.target.value)}
                        style={{ ...selSt, flex: '1 1 110px' }}>
                        <option value="">Tüm Katlar</option>
                        {katlar.map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                      </select>
                      <select value={filterCepheID} onChange={e => setFilterCepheID(e.target.value)}
                        style={{ ...selSt, flex: '1 1 110px' }}>
                        <option value="">Tüm Cepheler</option>
                        {cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                      </select>
                      <input type="text" placeholder="Daire ara..." value={daireSearch} onChange={e => setDaireSearch(e.target.value)}
                        style={{ ...selSt, flex: '1 1 130px' }} />
                    </div>

                    {/* Daire listesi */}
                    <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                      {filteredDaireler.length === 0 ? (
                        <div style={{ padding: '16px', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>Satışta daire bulunamadı</div>
                      ) : filteredDaireler.map(d => {
                        const selected = selectedDaireler.includes(d.daireID);
                        return (
                          <div key={d.daireID}
                            onClick={() => setSelectedDaireler(prev => prev.includes(d.daireID) ? prev.filter(id => id !== d.daireID) : [...prev, d.daireID])}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px',
                              cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                              background: selected ? '#eff6ff' : 'transparent', transition: 'background 0.12s',
                            }}
                            onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#f8fafc'; }}
                            onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}>
                            <input type="checkbox" readOnly checked={selected} style={{ accentColor: '#1a2440', width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>Daire {d.daireNo}</span>
                              <span style={{ fontSize: 12, color: '#64748b', marginLeft: 10 }}>
                                {[d.proje?.projeAdi, d.kat?.katAdi, d.cephe?.cepheAdi].filter(Boolean).join(' • ')}
                              </span>
                            </div>
                            {d.listeFiyati && (
                              <span style={{ fontSize: 12, fontWeight: 500, color: '#1a2440', whiteSpace: 'nowrap' }}>
                                {formatFiyat(d.listeFiyati)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <>
                    <input type="text" placeholder="Portföy ara (başlık, adres, ilan no)..." value={portfoySearch} onChange={e => setPortfoySearch(e.target.value)}
                      style={{ ...selSt, marginBottom: 10, width: '100%' }} />

                    {/* Portföy listesi */}
                    <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                      {filteredPortfoyler.length === 0 ? (
                        <div style={{ padding: '16px', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
                          {tumPortfoyler.length === 0 ? t('msg.yukleniyor') : 'Portföy bulunamadı'}
                        </div>
                      ) : filteredPortfoyler.map(p => {
                        const selected = selectedPortfoyler.includes(p.portfoyID);
                        return (
                          <div key={p.portfoyID}
                            onClick={() => setSelectedPortfoyler(prev => prev.includes(p.portfoyID) ? prev.filter(id => id !== p.portfoyID) : [...prev, p.portfoyID])}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px',
                              cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                              background: selected ? '#fff7ed' : 'transparent', transition: 'background 0.12s',
                            }}
                            onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#f8fafc'; }}
                            onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}>
                            <input type="checkbox" readOnly checked={selected} style={{ accentColor: '#e67d21', width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>{p.baslik}</span>
                              <span style={{ fontSize: 12, color: '#64748b', marginLeft: 10 }}>
                                {p.adres || `İlan No: ${p.ilanNo}`} · {p.tur}
                              </span>
                            </div>
                            {p.fiyat > 0 && (
                              <span style={{ fontSize: 12, fontWeight: 500, color: '#1a2440', whiteSpace: 'nowrap' }}>
                                {formatFiyat(p.fiyat)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ flexShrink: 0, padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowCreateModal(false)}
                style={{ padding: '9px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer', fontWeight: 500 }}>
                İptal
              </button>
              <button onClick={handleCreate} disabled={creating}
                style={{
                  padding: '9px 22px', border: 'none', borderRadius: 8,
                  background: creating ? '#94a3b8' : '#1a2440', color: '#fff',
                  fontSize: 13.5, cursor: creating ? 'not-allowed' : 'pointer', fontWeight: 600, transition: 'background 0.15s',
                }}
                onMouseEnter={e => { if (!creating) e.currentTarget.style.background = '#e67d21'; }}
                onMouseLeave={e => { if (!creating) e.currentTarget.style.background = '#1a2440'; }}>
                {creating ? 'Oluşturuluyor...' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Hızlı Müşteri Ekle Modal */}
      {showQuickCariModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setShowQuickCariModal(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }} />
          <div style={{ position: 'relative', width: '100%', maxWidth: 440, background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>Hızlı Müşteri Ekle</h3>
              <button onClick={() => setShowQuickCariModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#9ca3af', borderRadius: 6, display: 'flex', alignItems: 'center' }}>
                <X size={18} />
              </button>
            </div>
            {/* Body */}
            <form onSubmit={handleQuickAddCari}>
              <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelSt}>Ad Soyad <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text" style={inputSt} placeholder={t('label.adSoyad')}
                    value={quickCariForm.adiSoyadi}
                    onChange={e => setQuickCariForm(p => ({ ...p, adiSoyadi: e.target.value }))}
                    autoFocus maxLength={100}
                  />
                </div>
                <div>
                  <label style={labelSt}>Telefon <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text" style={inputSt} placeholder="05XX XXX XX XX"
                    value={quickCariForm.telefon1}
                    onChange={e => setQuickCariForm(p => ({ ...p, telefon1: e.target.value }))}
                    maxLength={20}
                  />
                </div>
                <div>
                  <label style={labelSt}>E-Posta</label>
                  <input
                    type="email" style={inputSt} placeholder="ornek@mail.com"
                    value={quickCariForm.ePosta}
                    onChange={e => setQuickCariForm(p => ({ ...p, ePosta: e.target.value }))}
                    maxLength={100}
                  />
                </div>
              </div>
              {/* Footer */}
              <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" onClick={() => setShowQuickCariModal(false)}
                  style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                  İptal
                </button>
                <button type="submit" disabled={quickCariSaving}
                  style={{ padding: '8px 20px', border: 'none', borderRadius: 8, background: quickCariSaving ? '#94a3b8' : '#1a2440', color: '#fff', fontSize: 13, cursor: quickCariSaving ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                  onMouseEnter={e => { if (!quickCariSaving) e.currentTarget.style.background = '#e67d21'; }}
                  onMouseLeave={e => { if (!quickCariSaving) e.currentTarget.style.background = '#1a2440'; }}>
                  {quickCariSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Rapor Modal */}
      {showRaporModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setShowRaporModal(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }} />
          <div style={{
            position: 'relative', width: '100%', maxWidth: 920, maxHeight: '94vh',
            background: '#fff', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', borderRadius: 16, overflow: 'hidden'
          }} onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={{ flexShrink: 0, padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a2440', margin: 0 }}>{t('sunum.sunumRaporu', 'Sunum Raporu')}</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '3px 0 0' }}>Tarih, proje ve daire bazlı sunum istatistikleri</p>
              </div>
              <button onClick={() => setShowRaporModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#9ca3af', borderRadius: 6, display: 'flex', alignItems: 'center' }}>
                <X size={20} />
              </button>
            </div>

            {/* Filtreler */}
            <div style={{ flexShrink: 0, padding: '14px 24px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>Başlangıç:</label>
                <TrDateInput value={raporBaslangic} onChange={e => setRaporBaslangic(e.target.value)}
                  style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12.5, outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>Bitiş:</label>
                <TrDateInput value={raporBitis} onChange={e => setRaporBitis(e.target.value)}
                  style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12.5, outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>Proje:</label>
                <select value={raporProjeFilter} onChange={e => setRaporProjeFilter(e.target.value)}
                  style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12.5, outline: 'none', background: '#fff' }}>
                  <option value="">{t('label.tumu')}</option>
                  {raporProjeler.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
                {raporSunumlar.length} sunum filtrelendi
              </span>
            </div>

            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

              {/* Özet Kartlar */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                {[
                  { label: 'Toplam Sunum', value: raporSunumlar.length, color: '#1a2440', bg: '#eff6ff', icon: '📋' },
                  { label: 'Unique Müşteri', value: new Set(raporSunumlar.map(s => s.cariID)).size, color: '#7c3aed', bg: '#f5f3ff', icon: '👤' },
                  { label: 'Sunulan Daire', value: raporSunumlar.reduce((acc, s) => acc + s.daireler.length, 0), color: '#0369a1', bg: '#f0f9ff', icon: '🏠' },
                  { label: 'Olumlu Sonuç', value: raporSunumlar.filter(s => s.durum === 'Olumlu Sonuçlandı').length, color: '#15803d', bg: '#f0fdf4', icon: '✅' },
                ].map(card => (
                  <div key={card.label} style={{ background: card.bg, borderRadius: 12, padding: '14px 16px', border: `1px solid ${card.bg}` }}>
                    <div style={{ fontSize: 20, marginBottom: 6 }}>{card.icon}</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: card.color, lineHeight: 1 }}>{card.value}</div>
                    <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 4, fontWeight: 500 }}>{card.label}</div>
                  </div>
                ))}
              </div>

              {/* Aylık Dağılım */}
              {Object.keys(raporByAy).length > 0 && (
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1a2440', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a2440" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    Aylık Sunum Dağılımı
                  </h3>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#1a2440', color: '#fff' }}>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Dönem</th>
                          <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600 }}>Sunum Sayısı</th>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Dağılım</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(raporByAy).sort((a, b) => b[0].localeCompare(a[0])).map(([ay, count], i) => {
                          const max = Math.max(...Object.values(raporByAy));
                          const [yil, mn] = ay.split('-');
                          const ayAdi = new Date(Number(yil), Number(mn) - 1, 1).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
                          return (
                            <tr key={ay} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                              <td style={{ padding: '9px 14px', color: '#374151', fontWeight: 500 }}>{ayAdi}</td>
                              <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                                <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{count}</span>
                              </td>
                              <td style={{ padding: '9px 14px' }}>
                                <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden', maxWidth: 200 }}>
                                  <div style={{ height: '100%', background: '#e67d21', borderRadius: 4, width: `${(count / max) * 100}%`, transition: 'width 0.3s' }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Proje Bazında */}
              {Object.keys(raporByProje).length > 0 && (
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1a2440', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a2440" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                    Proje Bazında Sunum
                  </h3>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#1a2440', color: '#fff' }}>
                          <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Proje</th>
                          <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600 }}>Sunum Sayısı</th>
                          <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600 }}>Sunulan Daire</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(raporByProje).sort((a, b) => b[1].count - a[1].count).map(([proje, data], i) => (
                          <tr key={proje} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                            <td style={{ padding: '9px 14px', color: '#374151', fontWeight: 500 }}>{proje}</td>
                            <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                              <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{data.count}</span>
                            </td>
                            <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                              <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{data.daireSayisi}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Satışta Daireler */}
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1a2440', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a2440" strokeWidth="2"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                  Satışta Daireler — Sunum Durumu
                  {raporProjeFilter && <span style={{ fontSize: 11, fontWeight: 400, color: '#64748b', marginLeft: 4 }}>({raporProjeFilter})</span>}
                </h3>

                {raporDairelerLoading ? (
                  <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8', fontSize: 13 }}>
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...
                  </div>
                ) : (() => {
                  const filteredRaporDaireler = raporDaireler.filter(d =>
                    !raporProjeFilter || d.proje?.projeAdi === raporProjeFilter
                  );
                  if (filteredRaporDaireler.length === 0) return (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8', fontSize: 13, background: '#f8fafc', borderRadius: 10, border: '1px dashed #e2e8f0' }}>
                      Satışta daire bulunamadı.
                    </div>
                  );
                  return (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                        <thead>
                          <tr style={{ background: '#1a2440', color: '#fff' }}>
                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Daire</th>
                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Proje / Kat / Cephe</th>
                            <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>Liste Fiyatı</th>
                            <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, whiteSpace: 'nowrap' }}>Sunum Sayısı</th>
                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>{t('musteri.sunumlar')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRaporDaireler.map((d, i) => {
                            const sunumBilgi = raporByDaire[d.daireID];
                            return (
                              <tr key={d.daireID} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', verticalAlign: 'top' }}>
                                <td style={{ padding: '9px 12px', fontWeight: 700, color: '#1a2440', whiteSpace: 'nowrap' }}>
                                  Daire {d.daireNo}
                                </td>
                                <td style={{ padding: '9px 12px', color: '#475569' }}>
                                  {[d.proje?.projeAdi, d.kat?.katAdi, d.cephe?.cepheAdi].filter(Boolean).join(' / ')}
                                </td>
                                <td style={{ padding: '9px 12px', textAlign: 'right', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                  {d.listeFiyati ? d.listeFiyati.toLocaleString('tr-TR') + ' ₺' : '—'}
                                </td>
                                <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                  {sunumBilgi ? (
                                    <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 20, padding: '2px 10px', fontSize: 11.5, fontWeight: 700 }}>
                                      {sunumBilgi.sunumSayisi}
                                    </span>
                                  ) : (
                                    <span style={{ color: '#d1d5db', fontSize: 11.5 }}>—</span>
                                  )}
                                </td>
                                <td style={{ padding: '9px 12px' }}>
                                  {sunumBilgi ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                      {sunumBilgi.sunumlar.map(sv => {
                                        const badge = DURUM_BADGE[sv.durum] || { bg: '#f1f5f9', color: '#475569' };
                                        const tepkiColor = sv.musteriTepkisi === 'Olumlu' ? '#15803d' : sv.musteriTepkisi === 'Olumsuz' ? '#b91c1c' : '#64748b';
                                        return (
                                          <div key={sv.sunumID} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                            <a
                                              href={`/musteri-sunumlari/${sv.sunumID}`}
                                              onClick={e => { e.preventDefault(); e.stopPropagation(); setShowRaporModal(false); navigate(`/musteri-sunumlari/${sv.sunumID}`); }}
                                              style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
                                              onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                                              onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                                            >
                                              {sv.baslik || `#${sv.sunumID}`}
                                            </a>
                                            {sv.cariAdSoyad && (
                                              <span style={{ fontSize: 11, color: '#94a3b8' }}>({sv.cariAdSoyad})</span>
                                            )}
                                            <span style={{ fontSize: 11, background: badge.bg, color: badge.color, borderRadius: 6, padding: '1px 6px', fontWeight: 600 }}>
                                              {sv.durum}
                                            </span>
                                            {sv.daireDurum && sv.daireDurum !== 'Belirsiz' && (
                                              <span style={{ fontSize: 11, background: '#f1f5f9', color: '#475569', borderRadius: 6, padding: '1px 6px' }}>
                                                {sv.daireDurum}
                                              </span>
                                            )}
                                            {sv.musteriTepkisi && sv.musteriTepkisi !== 'Belirsiz' && (
                                              <span style={{ fontSize: 11, color: tepkiColor, fontWeight: 600 }}>
                                                {sv.musteriTepkisi}
                                              </span>
                                            )}
                                            <span style={{ fontSize: 10.5, color: '#cbd5e1' }}>
                                              {formatDate(sv.tarih)}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <span style={{ fontSize: 12, color: '#d1d5db', fontStyle: 'italic' }}>Henüz sunulmadı</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ flexShrink: 0, padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowRaporModal(false)}
                style={{ padding: '9px 22px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 13.5, cursor: 'pointer', fontWeight: 500 }}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusteriSunumlari;
